import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { api } from '../../api/client';

const POLL_MS = 60_000;

/**
 * Count of initiatives that arrived in the Boîte de réception since this admin
 * last opened it, next to the sidebar link. Refreshed every minute, on window
 * focus and after saves, so a new arrival shows up while working — with a short
 * pulse when the number goes up. Hidden while on the inbox itself.
 */
export function InboxBadge({ onInbox }: { onInbox: boolean }) {
  const { t } = useT();
  const [count, setCount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const prev = useRef(0);

  const load = useCallback(() => {
    api.getInboxNewCount().then(({ count: n }) => {
      if (n > prev.current) { setPulse(true); setTimeout(() => setPulse(false), 1600); }
      prev.current = n;
      setCount(n);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    window.addEventListener('focus', load);
    window.addEventListener('pmo:data-changed', load);
    window.addEventListener('pmo:inbox-seen', load);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', load);
      window.removeEventListener('pmo:data-changed', load);
      window.removeEventListener('pmo:inbox-seen', load);
    };
  }, [load]);

  if (onInbox || count === 0) return null;
  return (
    <span className={`nav-badge${pulse ? ' is-pulsing' : ''}`} title={t('shell.inboxNew', { n: count })} aria-label={t('shell.inboxNew', { n: count })}>
      {count > 99 ? '99+' : count}
    </span>
  );
}
